import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { findOwningAgentId } from "./ownership.js";

/**
 * Shared Agent Portal page guard (Prompt 138) - every Agent Portal screen
 * (Invite Card, Org Chart, Workers, SOS/Abnormal, Footsteps, Zones,
 * Contacts) needs the exact same four states AgentInviteCardsPage.jsx
 * already established (Prompt 090): identity loading, identity error, "no
 * owning Agent scope" forbidden, and ready-with-a-real-agentId. Centralized
 * here instead of duplicated six more times. `Guarded` in App.jsx has no
 * role check of its own (RouteGuard only checks session presence) - this
 * hook is what actually keeps a non-Agent session (SuperAdmin/SuperAgent/
 * PLC, or an Agent scoped to nothing) out of every Agent Portal screen's
 * real content, on the frontend, in addition to the backend's own
 * independent scope enforcement on every API call these pages make.
 */
export function useAgentPortalGuard() {
  const { status: identityStatus, data: identity, error: identityError } = useBackofficeIdentity();

  if (identityStatus === "loading" || identityStatus === "idle") {
    return { status: "loading" };
  }

  if (identityStatus === "error") {
    return { status: "error", error: identityError };
  }

  const agentId = findOwningAgentId(identity.accessScopes);
  if (!agentId) {
    return { status: "forbidden", identity };
  }

  return { status: "ready", agentId, identity };
}
